/**
 * AUTH-6 / LRN-2: lesson progress must survive a dropped request. Reports are coalesced into one
 * pending payload (a bounded queue of one: the furthest point, OR'd completion), sent one at a
 * time, and retried with capped exponential backoff on network/5xx/CSRF failures. Completion is
 * only considered saved once the server accepted it, and repeated failures are surfaced.
 */
export type ProgressPayload = {
  watchedSeconds?: number;
  completed?: boolean;
  durationSec?: number;
  /** LRN-10: where playback is now — the latest report wins (it may go back). */
  positionSeconds?: number;
};

export type ProgressOutboxState = { completed: boolean; failing: boolean };

export type ProgressOutbox = {
  report(payload: ProgressPayload): void;
  state(): ProgressOutboxState;
  dispose(): void;
};

const BASE_DELAY_MS = 2_000;
const MAX_DELAY_MS = 60_000;
/** Failures in a row before the learner is told progress isn't saving. */
const FAILING_AFTER = 2;

function merge(a: ProgressPayload | null, b: ProgressPayload): ProgressPayload {
  if (!a) return { ...b };
  const out: ProgressPayload = { ...a };
  if (b.watchedSeconds !== undefined) {
    out.watchedSeconds = Math.max(out.watchedSeconds ?? 0, b.watchedSeconds);
  }
  if (b.completed) out.completed = true;
  if (b.durationSec !== undefined) out.durationSec = b.durationSec;
  if (b.positionSeconds !== undefined) out.positionSeconds = b.positionSeconds;
  return out;
}

type ErrorShape = {
  response?: { status?: number; data?: { error?: { code?: string } } };
  status?: number;
  code?: string;
};

/** Network errors, 5xx, 429 and a CSRF refusal are worth retrying; other 4xx are final. */
export function isRetryableProgressError(err: unknown): boolean {
  const e = (err ?? {}) as ErrorShape;
  const status = e.response?.status ?? e.status;
  if (status === undefined) return true;
  if (status >= 500 || status === 429 || status === 408) return true;
  const code = e.response?.data?.error?.code ?? e.code;
  return status === 403 && code === "CSRF_INVALID";
}

export function createProgressOutbox(options: {
  /** Resolves with the server's view of the lesson; `completed` there wins over the request. */
  send: (payload: ProgressPayload) => Promise<{ completed?: boolean } | void>;
  onStateChange?: (state: ProgressOutboxState) => void;
}): ProgressOutbox {
  let pending: ProgressPayload | null = null;
  let inFlight = false;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let failures = 0;
  let disposed = false;
  let current: ProgressOutboxState = { completed: false, failing: false };

  function setState(next: ProgressOutboxState) {
    if (next.completed === current.completed && next.failing === current.failing) return;
    current = next;
    options.onStateChange?.(current);
  }

  function scheduleRetry() {
    const delay = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** Math.max(0, failures - 1));
    retryTimer = setTimeout(() => {
      retryTimer = null;
      flush();
    }, delay);
  }

  function flush() {
    if (disposed || inFlight || retryTimer || !pending) return;
    const payload = pending;
    pending = null;
    inFlight = true;
    options
      .send(payload)
      .then(
        (result) => {
          failures = 0;
          const saved = result && typeof result.completed === "boolean" ? result.completed : null;
          setState({
            completed: current.completed || (saved ?? Boolean(payload.completed)),
            failing: false,
          });
        },
        (err: unknown) => {
          if (!isRetryableProgressError(err)) {
            failures = 0;
            setState({ completed: current.completed, failing: false });
            return;
          }
          failures += 1;
          pending = merge(payload, pending ?? {});
          setState({ completed: current.completed, failing: failures >= FAILING_AFTER });
          if (!disposed) scheduleRetry();
        },
      )
      .finally(() => {
        inFlight = false;
        flush();
      });
  }

  return {
    report(payload) {
      if (disposed) return;
      pending = merge(pending, payload);
      queueMicrotask(flush);
    },
    state: () => current,
    /** Stops retrying; whatever is still pending gets one last best-effort send. */
    dispose() {
      if (disposed) return;
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = null;
      if (pending && !inFlight) {
        const last = pending;
        pending = null;
        void options.send(last).catch(() => undefined);
      }
    },
  };
}
